import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  CommentReactionErrorModel,
  CommentReactionErrorTypesEnum,
} from '../../../../models/api/commentReactionModel';

/*
 * Props.
 */

interface ReactionBannersProps {
  onDismiss: (arg: string) => void;
  reactionErrors: ReadonlyArray<CommentReactionErrorModel>;
}

/*
 * Component.
 */

export const ReactionsBanners: FC<ReactionBannersProps> = ({onDismiss, reactionErrors}) => {
  if (!reactionErrors.length) {
    return null;
  }

  return (
    <>
      {reactionErrors.map((error) => {
        const onDismissError = () => onDismiss(error.id);
        return renderReactionErrorBanner(onDismissError, error);
      })}
    </>
  );
};

function renderReactionErrorBanner(onDismissError: () => void, error: CommentReactionErrorModel) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          key={error.id}
          color={theme.greys.shade70}
          priority={BannerPrioritiesEnum.TRANSIENT}
        >
          <BannerCloseAction onClick={onDismissError} />
          <BannerIcon iconName="warningFilledSmall" />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-reaction-title">Reaction limit reached</Trans>
            </BannerContentTitle>
            {renderBannerExplanation(error.type)}
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderBannerExplanation(type: CommentReactionErrorTypesEnum) {
  switch (type) {
    case CommentReactionErrorTypesEnum.MAX_PER_TEAMMATE:
      return (
        <Trans id="banner-reaction-maxperteammate">
          For performance reasons, you cannot add more than 20 emoji reactions to a comment.
        </Trans>
      );
    case CommentReactionErrorTypesEnum.MAX_UNIQUE:
      return (
        <Trans id="banner-reaction-maxunique">
          For performance reasons, there cannot be more than 30 emoji reactions on a comment.
        </Trans>
      );
    case CommentReactionErrorTypesEnum.MAX_UPVOTES:
      return (
        <Trans id="banner-reaction-maxupvotes">
          For performance reasons, no more than 50 teammates can upvote an emoji reaction on a comment.
        </Trans>
      );
    default:
      return <Trans id="banner-reaction-defaulterror">You cannot react to this comment.</Trans>;
  }
}
